import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { ENABLE_USER_BILLING } from '../config/featureFlags'
import { api } from '../lib/api'
import {
  BILLING_NUDGE_TZ,
  BILLING_PAYWALL_MODAL_LAST_DATE_KEY,
  billingPaywallNudgeNeedsUi,
  computeBillingPaywallNudge,
  dateStringInTimeZone,
} from '../lib/billingPaywallNudge'

function todayKstYmd() {
  return dateStringInTimeZone(new Date(), BILLING_NUDGE_TZ)
}

function readModalLastDate() {
  try {
    return localStorage.getItem(BILLING_PAYWALL_MODAL_LAST_DATE_KEY)
  } catch {
    return null
  }
}

function writeModalLastDate(ymd) {
  try {
    localStorage.setItem(BILLING_PAYWALL_MODAL_LAST_DATE_KEY, ymd)
  } catch {
    /* ignore */
  }
}

/** @param {import('../lib/billingPaywallNudge').BillingPaywallNudgeLevel} level */
function bannerTone(level) {
  if (level === 'warn7' || level === 'warn4') return 'bg-amber-50 border-amber-200 text-amber-950'
  return 'bg-red-50 border-red-200 text-red-950'
}

/** @param {import('../lib/billingPaywallNudge').BillingPaywallNudge} nudge */
function bannerMessage(nudge) {
  const end = nudge.endsAtYmd ? nudge.endsAtYmd.replace(/-/g, '.') : ''
  switch (nudge.level) {
    case 'suspended':
      return '구독이 정지되어 있습니다. 업무 기능을 계속 이용하려면 구독을 복구·결제해 주세요.'
    case 'expired':
      return '구독이 만료되었습니다. 서비스를 이용하려면 결제로 구독을 갱신해 주세요.'
    case 'lastDay':
      return end
        ? `구독 만료일이 오늘(${end}, KST 기준)입니다. 이용이 중단되지 않도록 결제를 진행해 주세요.`
        : '구독 만료일이 오늘(KST 기준)입니다. 이용이 중단되지 않도록 결제를 진행해 주세요.'
    case 'warn1':
      return end
        ? `구독이 ${end}(KST)에 만료됩니다. 하루 전 안내입니다. 결제를 진행해 주세요.`
        : '구독 만료 하루 전입니다. 결제를 진행해 주세요.'
    case 'warn4':
      return end
        ? `구독이 ${end}(KST)에 만료됩니다. 4일 전 안내입니다. 결제를 진행해 주세요.`
        : '구독 만료 4일 전입니다. 결제를 진행해 주세요.'
    case 'warn7':
      return end
        ? `구독이 ${end}(KST)에 만료됩니다. 7일 전 안내입니다. 결제를 진행해 주세요.`
        : '구독 만료 7일 전입니다. 결제를 진행해 주세요.'
    default:
      return ''
  }
}

/** @param {import('../lib/billingPaywallNudge').BillingPaywallNudge} nudge */
function modalMessage(nudge, billingUiEnabled) {
  const base = bannerMessage(nudge)
  if (billingUiEnabled) {
    return `${base} 아래 버튼에서 구독·결제 화면으로 이동할 수 있습니다.`
  }
  return `${base} 사용자 결제 화면이 비공개인 빌드에서는 내 정보로 이동해 계정을 확인하거나 관리자에게 문의해 주세요.`
}

/**
 * 로그인 셸: 구독 만료 임박·만료·정지 시 상단 배너(항상) + 모달(한국 달력일 기준 1일 1회).
 * 운영자·TRIAL·조직 전액(ORG_PAYS) 유효 계약은 제외. 서버 권한/쿼터 검증은 별도.
 */
export default function BillingPaywallShellNudge() {
  const { user } = useAuth()
  const roles = user?.roles || []
  const isOperator = roles.includes(Roles.OPERATOR)

  /** 구독 데이터는 BE에 항상 있으므로 플래그와 무관히 조회한다. (`/billing` UI만 플래그로 숨김) */
  const fetchEnabled = Boolean(user && !isOperator)
  const billingCtaPath = ENABLE_USER_BILLING ? '/billing/subscribe' : '/profile'
  const billingCtaBannerLabel = ENABLE_USER_BILLING ? '구독하기' : '내 정보로 이동'
  const billingCtaModalLabel = ENABLE_USER_BILLING ? '구독하기' : '내 정보 보기'

  const billingQuery = useQuery({
    queryKey: ['billing-me-paywall-nudge', user?.id],
    queryFn: async () => {
      const res = await api.get('/billing/me')
      return res?.data?.data ?? null
    },
    enabled: fetchEnabled,
    staleTime: 60_000,
    retry: false,
  })

  const nudge = React.useMemo(() => {
    if (!fetchEnabled || billingQuery.isError || !billingQuery.data) {
      return computeBillingPaywallNudge(null)
    }
    return computeBillingPaywallNudge(billingQuery.data)
  }, [fetchEnabled, billingQuery.isError, billingQuery.data])

  const showBanner = fetchEnabled && billingPaywallNudgeNeedsUi(nudge.level)
  const [modalOpen, setModalOpen] = React.useState(false)

  React.useEffect(() => {
    if (!showBanner) {
      setModalOpen(false)
      return
    }
    const today = todayKstYmd()
    if (!today) return
    const last = readModalLastDate()
    if (last === today) {
      setModalOpen(false)
      return
    }
    setModalOpen(true)
  }, [showBanner, nudge.level, nudge.endsAtYmd])

  const dismissModal = React.useCallback(() => {
    const today = todayKstYmd()
    if (today) writeModalLastDate(today)
    setModalOpen(false)
  }, [])

  if (!showBanner) return null

  return (
    <>
      <div className={`border-t px-4 py-2.5 text-center text-sm leading-snug ${bannerTone(nudge.level)}`}>
        <span>{bannerMessage(nudge)}</span>
        {' '}
        <Link className="font-semibold underline underline-offset-2 hover:opacity-90" to={billingCtaPath}>
          {billingCtaBannerLabel}
        </Link>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sgez-billing-paywall-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) dismissModal()
          }}
        >
          <div className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl">
            <h2 id="sgez-billing-paywall-title" className="text-base font-semibold text-slate-900">
              구독·결제 안내
            </h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {modalMessage(nudge, ENABLE_USER_BILLING)}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={dismissModal}
              >
                닫기
              </button>
              <Link
                to={billingCtaPath}
                className="rounded bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                onClick={dismissModal}
              >
                {billingCtaModalLabel}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
